import { sectionRenderer } from '@theme/section-renderer';

// Exact native product types: an oil named "Incienso" remains a fragrance.
const TYPES = {
  inciensos: ['Inciensos en varillas', 'Saquitos / Sahumerios'],
  fragancias: ['Aceite de fragancia', 'Colonia Aromática'],
  quemadores: ['Quemadores'],
};
const PARAM = 'categoria';

class IncenseFilters extends HTMLElement {
  connectedCallback() {
    this.root = this.closest('results-list');
    if (!this.root) return;
    this.addEventListener('click', this.onClick);
    window.addEventListener('popstate', this.onPopState);
    this.observer = new MutationObserver(() => this.schedule());
    this.observer.observe(this.root, { childList: true, subtree: true });
    this.schedule();
  }

  disconnectedCallback() {
    this.observer?.disconnect();
    this.abort?.abort();
    this.removeEventListener('click', this.onClick);
    window.removeEventListener('popstate', this.onPopState);
    cancelAnimationFrame(this.frame);
  }

  nativeQuery() {
    const url = new URL(location.href);
    url.searchParams.delete(PARAM);
    url.searchParams.delete('page');
    url.searchParams.sort();
    return url.search;
  }

  category() {
    const value = new URL(location.href).searchParams.get(PARAM);
    return Object.hasOwn(TYPES, value) ? value : 'todos';
  }

  schedule() {
    cancelAnimationFrame(this.frame);
    this.frame = requestAnimationFrame(() => this.refresh());
  }

  async refresh() {
    if (!this.isConnected) return;
    const grid = this.root.querySelector('.product-grid');
    const nativeQuery = this.nativeQuery();
    this.lastNativeQuery = nativeQuery;
    // Normally a single native page (currently 51 products). If the catalogue
    // exceeds Shopify's 250/page limit, finish loading before enabling filters.
    if (grid && Number(grid.dataset.lastPage) > 1 && (this.loadedGrid !== grid || this.loadedQuery !== nativeQuery)) {
      if (this.loadingGrid === grid) return;
      this.abort?.abort();
      const abort = this.abort = new AbortController();
      this.loadingGrid = grid;
      this.removeAttribute('data-ready');
      this.querySelector('[role="group"]').hidden = true;
      try {
        const cards = [...grid.querySelectorAll(':scope > [data-cx-product-type]')];
        const pages = new Map();
        for (const card of cards) {
          const page = Number(card.dataset.page);
          if (!pages.has(page)) pages.set(page, []);
          pages.get(page).push(card);
        }
        for (let page = 1; page <= Number(grid.dataset.lastPage); page++) {
          if (pages.has(page)) continue;
          const url = new URL(location.href);
          url.searchParams.set('page', String(page));
          url.searchParams.set('section_id', this.root.getAttribute('section-id'));
          const response = await fetch(url, { signal: abort.signal });
          if (!response.ok) throw new Error('Collection page unavailable');
          const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
          const nextCards = [...doc.querySelectorAll('.product-grid > [data-cx-product-type]')];
          if (!nextCards.length) throw new Error('Collection page missing');
          pages.set(page, nextCards);
        }
        if (abort.signal.aborted || !grid.isConnected || nativeQuery !== this.nativeQuery()) return;
        grid.replaceChildren(...[...pages].sort((a, b) => a[0] - b[0]).flatMap(([, items]) => items));
        this.loadedGrid = grid;
        this.loadedQuery = nativeQuery;
      } catch (error) {
        // Keep native pagination and all native products usable after a failure.
        if (error.name !== 'AbortError') console.warn('Crysalidas category filter: native pagination retained.');
        return;
      } finally {
        this.loadingGrid = null;
      }
    }
    this.apply();
  }

  apply() {
    const selected = this.category();
    const types = TYPES[selected];
    let count = 0;
    const cards = this.root.querySelectorAll('.product-grid > [data-cx-product-type]');
    for (const card of cards) {
      card.hidden = Boolean(types && !types.includes(card.dataset.cxProductType));
      if (!card.hidden) count++;
    }
    for (const button of this.querySelectorAll('[data-category]')) {
      button.setAttribute('aria-pressed', String(button.dataset.category === selected));
    }
    // Native price, availability and sorting forms retain the category on AJAX
    // renders. No changes to Shopify's shared facets or cart implementation.
    for (const form of this.root.querySelectorAll('form.facets__form')) {
      let input = form.querySelector(`input[name="${PARAM}"]`);
      if (selected === 'todos') { input?.remove(); continue; }
      if (!input) {
        input = document.createElement('input');
        input.type = 'hidden';
        input.name = PARAM;
        form.append(input);
      }
      input.value = selected;
    }
    for (const remove of this.root.querySelectorAll('facet-remove-component[data-url]')) {
      const url = new URL(remove.dataset.url, location.origin);
      if (selected === 'todos') url.searchParams.delete(PARAM);
      else url.searchParams.set(PARAM, selected);
      remove.dataset.url = url.pathname + url.search;
    }
    const label = `${count} ${count === 1 ? this.dataset.countLabel.replace(/s$/, '') : this.dataset.countLabel}`;
    for (const status of this.root.querySelectorAll('.products-count-wrapper [role="status"], [data-category-status]')) {
      if (status.textContent.trim() !== label) status.textContent = label;
    }
    for (const facets of this.root.querySelectorAll('facets-form-component')) facets.dataset.resultsCount = String(count);
    this.querySelector('[data-category-empty]').hidden = count > 0 || cards.length === 0;
    this.querySelector('[role="group"]').hidden = false;
    this.setAttribute('data-ready', '');
  }

  onClick = (event) => {
    const button = event.target.closest('button[data-category]');
    if (!button || !this.contains(button) || button.getAttribute('aria-pressed') === 'true') return;
    const url = new URL(location.href);
    const category = button.dataset.category;
    if (category === 'todos') url.searchParams.delete(PARAM);
    else url.searchParams.set(PARAM, category);
    url.searchParams.delete('page');
    history.pushState({ ...history.state, cxCategory: category }, '', url);
    this.apply();
  };

  onPopState = () => {
    if (location.pathname !== new URL(this.dataset.collectionUrl, location.origin).pathname) return;
    if (this.lastNativeQuery !== this.nativeQuery()) {
      sectionRenderer.renderSection(this.root.getAttribute('section-id')).then(() => this.schedule());
    } else this.apply();
  };
}

if (!customElements.get('cx-incense-filters')) customElements.define('cx-incense-filters', IncenseFilters);
